import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DataTable } from "../../components/DataTable";
import { StatusBadge } from "../../components/StatusBadge";
import { ArrowLeft, Plus, Search, Trash2, X } from "lucide-react";
import { useGroup, useAddDevicesToGroup, useRemoveDeviceFromGroup, type GroupDeviceRow } from "../../api/groups";
import {
  DEVICE_DATE_RANGE_FILTERS,
  deviceDateFilters,
  deviceFilterParams,
  useDevices,
  useDeviceTotal,
  type DeviceFilters,
  type DeviceSearchField,
} from "../../api/devices";
import type { DayRange } from "../../lib/dayRange";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { formatRange, totalPagesFor, useCursorPaging } from "../../hooks/useCursorPaging";
import { extractErrorMessage } from "../../api/errors";

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const ADD_PAGE_SIZE = 50;
const SEARCH_DEBOUNCE_MS = 300;

// The picker's single search box looks up an IMEI when the term is all
// digits and a device name otherwise.
function pickerSearch(term: string): Pick<DeviceFilters, "search" | "searchField"> {
  const trimmed = term.trim();
  return { search: trimmed, searchField: /^\d+$/.test(trimmed) ? "imei" : "deviceName" };
}

export function GroupDetailPage() {
  const { id = "" } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const groupQuery = useGroup(id);
  const group = groupQuery.data?.data;

  // Members: paged, searched, filtered and counted on the server (the
  // tenant device list scoped to this group), so a group of thousands is
  // browsable and searchable in full.
  const [memberSearch, setMemberSearch] = useState("");
  const [memberSearchField, setMemberSearchField] = useState<DeviceSearchField>("imei");
  const [memberStatus, setMemberStatus] = useState("");
  const [memberDates, setMemberDates] = useState<Record<string, DayRange>>({});
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const debouncedMemberSearch = useDebouncedValue(memberSearch, SEARCH_DEBOUNCE_MS);
  const memberFilters: DeviceFilters = {
    groupId: id,
    search: debouncedMemberSearch,
    searchField: memberSearchField,
    status: memberStatus || undefined,
    ...deviceDateFilters(memberDates),
  };
  const memberPaging = useCursorPaging(JSON.stringify([deviceFilterParams(memberFilters).toString(), pageSize]));
  const devicesQuery = useDevices({ ...memberFilters, limit: pageSize, cursor: memberPaging.cursor }, { enabled: !!id });
  const memberTotalQuery = useDeviceTotal(memberFilters, { enabled: !!id });
  const devices: GroupDeviceRow[] = devicesQuery.data?.data ?? [];
  const membersHasMore = !!devicesQuery.data?.pagination.hasMore;
  const memberTotal = memberTotalQuery.isPlaceholderData ? undefined : memberTotalQuery.data?.total;
  const memberTotalIsLowerBound = !memberTotalQuery.isPlaceholderData && !!memberTotalQuery.data?.isLowerBound;

  const addMutation = useAddDevicesToGroup(id);
  const removeMutation = useRemoveDeviceFromGroup(id);

  const [removeTarget, setRemoveTarget] = useState<GroupDeviceRow | null>(null);
  const [removeError, setRemoveError] = useState("");

  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState<string[]>([]);
  const [addQuery, setAddQuery] = useState("");
  const [addError, setAddError] = useState("");
  const [flash, setFlash] = useState<string | null>(null);

  // Only unassigned devices can be added — a device belongs to at most one
  // group. The server filters for them across the whole tenant, so the
  // picker can reach every one, not just those on the first page.
  const debouncedAddQuery = useDebouncedValue(addQuery, SEARCH_DEBOUNCE_MS);
  const addFilters: DeviceFilters = { unallocated: true, ...pickerSearch(debouncedAddQuery) };
  const addPaging = useCursorPaging(deviceFilterParams(addFilters).toString());
  const addableQuery = useDevices({ ...addFilters, limit: ADD_PAGE_SIZE, cursor: addPaging.cursor }, { enabled: showAdd });
  const addable = addableQuery.data?.data ?? [];
  const addableHasMore = !!addableQuery.data?.pagination.hasMore;

  function toggle(imei: string) {
    setSelected((s) => (s.includes(imei) ? s.filter((x) => x !== imei) : [...s, imei]));
  }

  function closeAdd() {
    setShowAdd(false);
    setSelected([]);
    setAddQuery("");
    setAddError("");
  }

  async function handleRemove() {
    if (!removeTarget) return;
    setRemoveError("");
    const { imei, deviceName } = removeTarget;
    try {
      await removeMutation.mutateAsync(imei);
      setRemoveTarget(null);
      setFlash(`Removed ${deviceName || imei} from the group.`);
      setTimeout(() => setFlash(null), 4000);
    } catch (err) {
      setRemoveError(extractErrorMessage(err, "Failed to remove device"));
    }
  }

  async function handleAdd() {
    setAddError("");
    try {
      const res = await addMutation.mutateAsync(selected);
      const added = res.data.added?.length ?? 0;
      const skipped = res.data.skipped ? Object.keys(res.data.skipped).length : 0;
      setFlash(`Added ${added} device${added === 1 ? "" : "s"}${skipped ? `, skipped ${skipped}` : ""}.`);
      setTimeout(() => setFlash(null), 4000);
      closeAdd();
    } catch (err) {
      setAddError(extractErrorMessage(err, "Failed to add devices"));
    }
  }

  const columns = [
    { key: "imei", header: "IMEI", sortable: true },
    { key: "deviceName", header: "Name", sortable: true },
    { key: "status", header: "Status", render: (row: GroupDeviceRow) => <StatusBadge status={row.status} /> },
    { key: "firmwareVersion", header: "Firmware", render: (row: GroupDeviceRow) => row.firmwareVersion || <span className="text-gray-400 dark:text-riot-navy-400">-</span> },
    { key: "lastSeen", header: "Last Seen", render: (row: GroupDeviceRow) => row.lastSeen ? new Date(row.lastSeen).toLocaleString() : <span className="text-gray-400 dark:text-riot-navy-400">-</span> },
    {
      key: "actions",
      header: "",
      render: (row: GroupDeviceRow) => (
        <button
          onClick={() => {
            setRemoveError("");
            setRemoveTarget(row);
          }}
          className="rounded p-1 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/30 dark:hover:text-red-400"
          aria-label={`Remove ${row.deviceName || row.imei} from group`}
        >
          <Trash2 size={14} />
        </button>
      ),
    },
  ];

  return (
    <div>
      <button onClick={() => navigate("/groups")} className="mb-4 flex items-center gap-1 text-xs text-riot-gold-500 hover:underline">
        <ArrowLeft size={14} /> Back to groups
      </button>

      {group && (
        <div className="mb-4 rounded-lg border border-gray-200 bg-white p-4 dark:border-riot-navy-700 dark:bg-riot-navy-800">
          <div className="flex items-baseline justify-between">
            <div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">{group.name}</h2>
              <p className="mt-0.5 text-xs text-gray-500 dark:text-riot-navy-300">
                <span className="capitalize">{group.type}</span> group · {group.deviceCount} device{group.deviceCount === 1 ? "" : "s"}
              </p>
            </div>
            <div className="text-right text-[11px] text-gray-400 dark:text-riot-navy-400">
              Created {new Date(group.createdAt).toLocaleDateString()}
            </div>
          </div>
          {group.description && (
            <p className="mt-2 text-xs text-gray-600 dark:text-riot-navy-200">{group.description}</p>
          )}
        </div>
      )}

      <DataTable
        title={group ? `Devices in ${group.name}` : "Devices"}
        totalCount={memberTotal}
        columns={columns}
        data={devices}
        keyExtractor={(d) => d.imei}
        selectable
        serverSide
        filters={{
          searchPlaceholder: "Search devices",
          searchFields: [
            { value: "imei", label: "IMEI" },
            { value: "deviceName", label: "Name" },
          ],
          // The statuses the list shows (derived from last-seen time).
          statusOptions: [
            { value: "online", label: "Online" },
            { value: "stale", label: "Stale" },
            { value: "offline", label: "Offline" },
          ],
          dateRangeFilters: DEVICE_DATE_RANGE_FILTERS,
        }}
        onFilterChange={(f) => {
          setMemberSearch(f.search);
          setMemberSearchField(f.searchField as DeviceSearchField);
          setMemberStatus(f.status);
          setMemberDates(f.dateRanges);
        }}
        pagination={{
          page: memberPaging.page,
          totalPages: totalPagesFor(memberPaging.page, pageSize, membersHasMore, memberTotal),
          count: devices.length,
          hasMore: membersHasMore,
          rangeLabel: formatRange(memberPaging.offset, devices.length, memberTotal, memberTotalIsLowerBound),
          pageSize,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          onPageSizeChange: setPageSize,
          onNext: () => {
            const next = devicesQuery.data?.pagination.cursor;
            if (next && !devicesQuery.isPlaceholderData) memberPaging.next(next, devices.length);
          },
          onPrev: memberPaging.prev,
          onFirst: memberPaging.first,
        }}
        actions={
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-1.5 rounded-md bg-riot-gold-400 px-3.5 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600"
          >
            <Plus size={14} /> Add Devices
          </button>
        }
      />

      {flash && (
        <div
          role="status"
          className="fixed bottom-6 right-6 z-50 rounded-md bg-riot-navy-900 px-4 py-2 text-xs text-white shadow-lg dark:bg-riot-gold-400 dark:text-riot-navy-900"
        >
          {flash}
        </div>
      )}

      {removeTarget && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            role="dialog"
            aria-modal="true"
            className="w-[min(440px,100%)] rounded-lg border border-gray-200 bg-white p-5 shadow-xl dark:border-riot-navy-700 dark:bg-riot-navy-800"
          >
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Remove device from group</h3>
            <p className="mt-2 text-xs text-gray-600 dark:text-riot-navy-200">
              Remove <span className="font-medium">{removeTarget.deviceName || removeTarget.imei}</span>
              {group ? ` from ${group.name}` : ""}? The device stays registered — it just leaves this group and can be re-added.
            </p>
            {removeError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{removeError}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button
                onClick={() => setRemoveTarget(null)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
              >
                Cancel
              </button>
              <button
                onClick={handleRemove}
                disabled={removeMutation.isPending}
                className="rounded-md bg-red-600 px-4 py-1.5 text-xs font-medium text-white hover:bg-red-700 disabled:opacity-50"
              >
                {removeMutation.isPending ? "Removing…" : "Remove"}
              </button>
            </div>
          </div>
        </div>
      )}

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div
            role="dialog"
            aria-modal="true"
            className="flex max-h-[80vh] w-[min(560px,100%)] flex-col rounded-lg border border-gray-200 bg-white p-5 shadow-xl dark:border-riot-navy-700 dark:bg-riot-navy-800"
          >
            <div className="mb-3 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
                Add devices{group ? ` to ${group.name}` : ""}
              </h3>
              <button onClick={closeAdd} aria-label="Close" className="text-gray-400 hover:text-gray-600 dark:hover:text-white">
                <X size={16} />
              </button>
            </div>

            <div className="relative mb-3">
              <Search size={14} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={addQuery}
                onChange={(e) => setAddQuery(e.target.value)}
                placeholder="Search unassigned devices"
                className="w-full rounded-md border border-gray-300 bg-white py-2 pl-9 pr-3 text-sm dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              />
            </div>

            <div className="flex-1 overflow-y-auto rounded-md border border-gray-200 dark:border-riot-navy-700">
              {addableQuery.isLoading ? (
                <div className="px-3 py-8 text-center text-xs text-gray-400 dark:text-riot-navy-400">Loading devices…</div>
              ) : addable.length === 0 ? (
                <div className="px-3 py-8 text-center text-xs text-gray-400 dark:text-riot-navy-400">
                  {addableHasMore ? (
                    <>
                      No matches in the devices checked so far.{" "}
                      <button
                        type="button"
                        onClick={() => {
                          const next = addableQuery.data?.pagination.cursor;
                          if (next && !addableQuery.isPlaceholderData) addPaging.next(next, 0);
                        }}
                        className="font-medium text-riot-gold-600 hover:text-riot-gold-700 dark:text-riot-gold-400"
                      >
                        Keep searching
                      </button>
                    </>
                  ) : addQuery.trim() ? "No unassigned devices match your search." : "No unassigned devices to add."}
                </div>
              ) : (
                addable.map((dev) => (
                  <label
                    key={dev.imei}
                    className="flex cursor-pointer items-center gap-3 border-b border-gray-100 px-3 py-2 text-xs last:border-0 hover:bg-gray-50 dark:border-riot-navy-700/50 dark:hover:bg-riot-navy-700/40"
                  >
                    <input type="checkbox" checked={selected.includes(dev.imei)} onChange={() => toggle(dev.imei)} />
                    <span className="flex-1">
                      <span className="block font-medium text-gray-800 dark:text-riot-navy-100">{dev.deviceName || dev.imei}</span>
                      <span className="block font-mono text-[11px] text-gray-400 dark:text-riot-navy-400">{dev.imei}</span>
                    </span>
                    <StatusBadge status={dev.status} />
                  </label>
                ))
              )}
            </div>

            {(addPaging.page > 1 || addableHasMore) && (
              <div className="mt-2 flex items-center justify-end gap-2 text-xs text-gray-500 dark:text-riot-navy-300">
                <span>Page {addPaging.page}</span>
                <button
                  type="button"
                  onClick={addPaging.prev}
                  disabled={addPaging.page <= 1}
                  className="rounded border border-gray-300 px-2 py-0.5 disabled:opacity-40 dark:border-riot-navy-600"
                >
                  Previous
                </button>
                <button
                  type="button"
                  onClick={() => {
                    const next = addableQuery.data?.pagination.cursor;
                    if (next && !addableQuery.isPlaceholderData) addPaging.next(next, addable.length);
                  }}
                  disabled={!addableHasMore}
                  className="rounded border border-gray-300 px-2 py-0.5 disabled:opacity-40 dark:border-riot-navy-600"
                >
                  Next
                </button>
              </div>
            )}

            {addError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{addError}</p>}

            <div className="mt-4 flex items-center justify-end gap-2">
              <span className="mr-auto text-xs text-gray-500 dark:text-riot-navy-300">{selected.length} selected</span>
              <button
                onClick={closeAdd}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
              >
                Cancel
              </button>
              <button
                onClick={handleAdd}
                disabled={selected.length === 0 || addMutation.isPending}
                className="rounded-md bg-riot-gold-400 px-4 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 disabled:opacity-50"
              >
                {addMutation.isPending ? "Adding…" : `Add ${selected.length || ""}`.trim()}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
